/**
 * Version Check Utility
 * 
 * Detects when a new deployment is available and forces a page reload
 * to ensure users are always running the latest version of the application.
 * 
 * This prevents issues where users have cached old HTML that references
 * chunks that no longer exist after a new deployment.
 */

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_STORAGE_KEY = 'app_version';
const VERSION_CHECK_KEY = 'last_version_check';

/**
 * Get the current build version from the build metadata
 * In production, this is injected by the build process
 */
const getCurrentVersion = () => {
  // Vite injects this during build
  if (import.meta.env.VITE_APP_VERSION) {
    return import.meta.env.VITE_APP_VERSION;
  }
  
  // Fallback: use build timestamp
  if (import.meta.env.VITE_BUILD_TIME) {
    return import.meta.env.VITE_BUILD_TIME;
  }
  
  // Development fallback
  return 'dev-' + Date.now();
};

/**
 * Get stored version from localStorage
 */
const getStoredVersion = () => {
  try {
    return localStorage.getItem(VERSION_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

/**
 * Store current version in localStorage
 */
const storeVersion = (version) => {
  try {
    localStorage.setItem(VERSION_STORAGE_KEY, version);
  } catch (e) {
    // Ignore storage errors
  }
};

/**
 * Get last version check time
 */
const getLastCheckTime = () => {
  try {
    const time = localStorage.getItem(VERSION_CHECK_KEY);
    return time ? parseInt(time, 10) : 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Update last version check time
 */
const updateLastCheckTime = () => {
  try {
    localStorage.setItem(VERSION_CHECK_KEY, Date.now().toString());
  } catch (e) {
    // Ignore storage errors
  }
};

/**
 * Check if version check is due
 */
const isVersionCheckDue = () => {
  const lastCheck = getLastCheckTime();
  const timeSinceLastCheck = Date.now() - lastCheck;
  return timeSinceLastCheck >= VERSION_CHECK_INTERVAL;
};

/**
 * Fetch the current version from the server
 * This checks the actual deployed index.html to detect new deployments
 */
const fetchServerVersion = async () => {
  try {
    // Fetch index.html with cache-busting to get latest version
    const response = await fetch(`/?_v=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
    });
    
    // Get etag or last-modified as version indicator
    const etag = response.headers.get('etag');
    const lastModified = response.headers.get('last-modified');
    
    if (etag) {
      return `etag-${etag}`;
    }
    
    if (lastModified) {
      return `modified-${lastModified}`;
    }
    
    // Fallback: use current timestamp
    return `check-${Date.now()}`;
  } catch (error) {
    console.warn('[VersionCheck] Failed to fetch server version:', error);
    return null;
  }
};

/**
 * Check if a new version is available
 * @returns {Promise<boolean>} - True if new version is available
 */
export const checkForNewVersion = async () => {
  // Skip version check if not due yet
  if (!isVersionCheckDue()) {
    return false;
  }
  
  updateLastCheckTime();
  
  const currentVersion = getCurrentVersion();
  const serverVersion = await fetchServerVersion();
  
  // If we couldn't get server version, assume no update
  if (!serverVersion) {
    return false;
  }
  
  // Store current version if not stored
  const storedVersion = getStoredVersion();
  if (!storedVersion) {
    storeVersion(currentVersion);
    return false;
  }
  
  // Check if server version is different from stored version
  // This indicates a new deployment
  if (serverVersion !== storedVersion) {
    return true;
  }
  
  return false;
};

/**
 * Reload the page to load the new version
 * @param {Object} options - Options for reload
 * @param {boolean} options.force - Force reload even if not confirmed
 */
export const reloadToNewVersion = (options = {}) => {
  const { force = false } = options;
  
  
  // Clear stored version to force fresh check on next load
  try {
    localStorage.removeItem(VERSION_STORAGE_KEY);
    localStorage.removeItem(VERSION_CHECK_KEY);
  } catch (e) {
    // Ignore storage errors
  }
  
  // Add cache-busting parameter
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());
  
  // Perform the reload
  if (force) {
    window.location.replace(url.toString());
  } else {
    window.location.href = url.toString();
  }
};

/**
 * Show a notification to the user about the new version
 * and reload when they confirm
 */
export const promptForReload = () => {
  // Use custom event to notify the app
  const event = new CustomEvent('appUpdateAvailable', {
    detail: {
      reload: reloadToNewVersion,
    },
  });
  
  window.dispatchEvent(event);
  
  // Also show a simple confirmation if no handler is registered
  setTimeout(() => {
    if (window.confirm('A new version of the application is available. Reload now to update?')) {
      reloadToNewVersion();
    }
  }, 100);
};

/**
 * Start periodic version checking
 * @param {Object} options - Options
 * @param {boolean} options.autoReload - Automatically reload on new version (default: false)
 * @param {Function} options.onUpdate - Callback when update is available
 */
export const startVersionChecking = (options = {}) => {
  const { autoReload = false, onUpdate = null } = options;
  
  // Check immediately on startup (but don't auto-reload by default)
  const initialCheck = async () => {
    const hasNewVersion = await checkForNewVersion();
    
    if (hasNewVersion) {
      if (onUpdate) {
        onUpdate(reloadToNewVersion);
      } else if (autoReload) {
        reloadToNewVersion();
      }
      // Don't prompt immediately on startup - let the app load normally
    }
  };
  
  // Run initial check after a delay to allow app to fully load
  setTimeout(() => {
    initialCheck();
  }, 10000); // Wait 10 seconds before first check
  
  // Set up periodic checking (but don't auto-reload)
  const intervalId = setInterval(async () => {
    const hasNewVersion = await checkForNewVersion();
    
    if (hasNewVersion) {
      if (onUpdate) {
        onUpdate(reloadToNewVersion);
      } else if (autoReload) {
        reloadToNewVersion();
      }
      // Don't prompt - just log. User can refresh manually if needed.
    }
  }, VERSION_CHECK_INTERVAL);
  
  // Return cleanup function
  return () => clearInterval(intervalId);
};

/**
 * Initialize version checking on app startup
 * This should be called early in the app lifecycle
 * NOTE: Auto-reload is DISABLED by default to prevent infinite reload loops
 */
export const initVersionCheck = () => {
  // Store current version on startup
  const currentVersion = getCurrentVersion();
  storeVersion(currentVersion);
  
  // Start periodic checking WITHOUT auto-reload
  return startVersionChecking({
    autoReload: false, // DISABLED: Prevent infinite reload loops
  });
};

/**
 * Manually trigger a version check
 * Useful for checking after user actions or when app regains focus
 */
export const manualVersionCheck = async () => {
  // Reset last check time to force immediate check
  try {
    localStorage.removeItem(VERSION_CHECK_KEY);
  } catch (e) {
    // Ignore
  }
  
  const hasNewVersion = await checkForNewVersion();
  
  if (hasNewVersion) {
    reloadToNewVersion();
    return true;
  }
  
  return false;
};

/**
 * Hook to check version when window regains focus
 * This catches updates when the user returns to the app
 */
export const setupVisibilityChangeHandler = () => {
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // Page became visible, check for updates
      manualVersionCheck().catch(error => {
        console.warn('[VersionCheck] Error checking version on visibility change:', error);
      });
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Return cleanup function
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

export default {
  checkForNewVersion,
  reloadToNewVersion,
  promptForReload,
  startVersionChecking,
  initVersionCheck,
  manualVersionCheck,
  setupVisibilityChangeHandler,
  getCurrentVersion,
};
